import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import SEOHead from '../../../components/feature/SEOHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const windowSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Interior & Lower Level Window Cleaning Services',
  description: 'Professional interior and lower-level window cleaning for commercial properties in Louisville, KY. Streak-free results, hard water stain removal, glass partition maintenance.',
  provider: {
    '@type': 'LocalBusiness',
    name: 'First Insight Cleaning',
    url: siteUrl,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Louisville',
      addressRegion: 'KY',
      addressCountry: 'US',
    },
  },
  areaServed: { '@type': 'City', name: 'Louisville' },
  serviceType: 'Window Cleaning',
};

export default function WindowCleaningPage() {
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 10); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Window Cleaning Services Louisville KY | First Insight Cleaning"
        description="Expert interior &amp; lower-level window cleaning for commercial properties in Louisville, KY. Streak-free results, hard water stain removal, glass partition &amp; storefront cleaning. Free quote available."
        keywords="window cleaning Louisville KY, commercial window cleaning Kentucky, interior glass cleaning, First Insight Cleaning"
        canonical={`${siteUrl}/services/window-cleaning`}
        schemaJson={windowSchema}
      />
      <Navbar scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_674195374ccd8667ef34a6f77d40538b_f50a9186.jpg"
            alt="Interior and Lower Level Window Cleaning Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">Interior &amp; Lower Level Window Cleaning</h1>
          <p className="text-base md:text-xl max-w-2xl mx-auto">Spotless interior windows and lower-level exterior glass for commercial properties</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-slate-800 mb-6">Professional Interior &amp; Lower Level Window Cleaning</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-4">
              First Insight Cleaning provides expert interior and lower-level window cleaning services for commercial properties. We ensure streak-free, spotless windows that enhance your workspace appearance and allow natural light to brighten your environment. Our trained technicians use professional-grade equipment and eco-friendly cleaning solutions to deliver exceptional results on all interior glass surfaces and accessible exterior windows.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              From office partitions and interior glass walls to ground-floor storefronts and lower-level exterior windows, we have the expertise to handle all your window cleaning needs. Our services include interior window cleaning, frame and sill cleaning, glass partition maintenance, and removal of hard water stains and buildup.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Interior Window Cleaning</h3>
              <p className="text-slate-600">Thorough cleaning of all interior windows, glass partitions, and glass doors throughout your facility.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-store-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Storefront Window Care</h3>
              <p className="text-slate-600">Regular maintenance for retail storefronts ensuring crystal-clear displays that attract customers.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-drop-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Hard Water Stain Removal</h3>
              <p className="text-slate-600">Specialized treatments to remove mineral deposits and restore window clarity.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-paint-brush-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Frame &amp; Sill Cleaning</h3>
              <p className="text-slate-600">Comprehensive cleaning of window frames, tracks, and sills for complete maintenance.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-layout-grid-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Glass Partition Maintenance</h3>
              <p className="text-slate-600">Professional cleaning of interior glass walls, conference room partitions, and office dividers.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-lg">
              <div className="w-14 h-14 bg-teal-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Scheduled Maintenance</h3>
              <p className="text-slate-600">Regular cleaning programs customized to your building's needs and budget.</p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Why Choose Our Window Cleaning</h2>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Trained and insured window cleaning professionals</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Professional-grade tools for interior and lower-level glass</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Streak-free, spotless results guaranteed</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Eco-friendly cleaning solutions safe for indoor use</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Flexible scheduling to minimize disruption</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-slate-600">Competitive pricing with no hidden fees</span>
                </li>
              </ul>
            </div>
            <div className="h-[400px]">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_22bd1c6ea00f82cf10e326910e2c2fdf_196740ba.jpg"
                alt="Interior Window Cleaning in Action"
                className="w-full h-full object-cover rounded-lg shadow-lg object-top"
              />
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-teal-500 rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready for Crystal-Clear Windows?</h2>
            <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
              Schedule your professional interior and lower-level window cleaning service today
            </p>
            <Link 
              to="/#quote"
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
